import React, { useState } from 'react';
import { TradeDetail } from '../../types';
import './TradeHistory.css';

interface TradeHistoryProps {
  trades: TradeDetail[];
  backtestId?: number;
  dateRange?: string;
}

const TradeHistory: React.FC<TradeHistoryProps> = ({ trades, backtestId, dateRange }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const tradesPerPage = 15;

  if (trades.length === 0) {
    return (
      <div className="trade-history">
        <h3>Trade History</h3>
        <div className="no-results">
          No trades available. Run or load a backtest to see trades.
        </div>
      </div>
    );
  }

  // Pagination logic
  const indexOfLastTrade = currentPage * tradesPerPage;
  const indexOfFirstTrade = indexOfLastTrade - tradesPerPage;
  const currentTrades = trades.slice(indexOfFirstTrade, indexOfLastTrade);
  const totalPages = Math.ceil(trades.length / tradesPerPage);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const exportToExcel = () => {
    // Create CSV content
    const headers = [
      'Direction',
      'Entry Time',
      'Entry Price',
      'Entry Reason',
      'Exit Time',
      'Exit Price',
      'Exit Reason',
      'P/L',
      'Max Profit',
      'Max Drawdown',
      'ML Approved'
    ];

    const csvRows = [headers.join(',')];

    trades.forEach((trade, index) => {
      const entryTime = trade.entryTime || trade.entry_time || '';
      const entryPrice = trade.entryPrice || trade.entry_price || 0;
      const entryReason = (trade as any).entryReason || (trade as any).entry_reason || '';
      const exitTime = trade.exitTime || trade.exit_time || '';
      const exitPrice = trade.exitPrice || trade.exit_price || 0;
      const exitReason = trade.exitReason || trade.exit_reason || '';
      const profitLoss = trade.profitLoss !== undefined ? trade.profitLoss : trade.profit_loss || 0;
      const maxProfit = (trade as any).maxProfit || (trade as any).max_profit || (trade as any).maxProfitReached || (trade as any).max_profit_reached || 0;
      const maxDrawdown = (trade as any).maxDrawdown || (trade as any).max_drawdown || (trade as any).maxDrawdownInTrade || (trade as any).max_drawdown_in_trade || 0;
      const mlApproved = trade.mlApproved !== undefined ? trade.mlApproved : trade.ml_approved;

      const row = [
        trade.direction || '',
        entryTime ? new Date(entryTime).toLocaleString() : '',
        entryPrice,
        entryReason,
        exitTime ? new Date(exitTime).toLocaleString() : '',
        exitPrice,
        exitReason,
        profitLoss,
        maxProfit,
        maxDrawdown,
        mlApproved === null ? 'N/A' : mlApproved ? 'Yes' : 'No'
      ];

      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `backtest_${backtestId || 'trades'}_export.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <div className="history-header">
        <h3>Trade History ({trades.length} trades){dateRange}</h3>
        <button onClick={exportToExcel} className="export-button">
          Export to Excel
        </button>
      </div>

      <div className="table-wrapper">
        <table className="trades-table">
            <thead>
            <tr>
              <th>Direction</th>
              <th>Entry Time</th>
              <th>Entry Price</th>
              <th>Entry Reason</th>
              <th>Exit Time</th>
              <th>Exit Price</th>
              <th>Exit Reason</th>
              <th>P/L</th>
              <th>Max Profit</th>
              <th>Max Drawdown</th>
              <th>ML Approved</th>
            </tr>
          </thead>
          <tbody>
            {currentTrades.map((trade, index) => {
              const actualIndex = indexOfFirstTrade + index;
              const entryTime = trade.entryTime || trade.entry_time;
              const entryPrice = trade.entryPrice || trade.entry_price;
              const entryReason = (trade as any).entryReason || (trade as any).entry_reason;
              const exitTime = trade.exitTime || trade.exit_time;
              const exitPrice = trade.exitPrice || trade.exit_price;
              const exitReason = trade.exitReason || trade.exit_reason;
              const profitLoss = trade.profitLoss !== undefined ? trade.profitLoss : trade.profit_loss;
              const maxProfit = (trade as any).maxProfit || (trade as any).max_profit || (trade as any).maxProfitReached || (trade as any).max_profit_reached;
              const maxDrawdown = (trade as any).maxDrawdown || (trade as any).max_drawdown || (trade as any).maxDrawdownInTrade || (trade as any).max_drawdown_in_trade;
              const mlApproved = trade.mlApproved !== undefined ? trade.mlApproved : trade.ml_approved;

              return (
                <tr key={trade.trade_id || trade.tradeId || actualIndex}>
                  <td className={trade.direction === 'BUY' ? 'direction-buy' : 'direction-sell'}>
                    {trade.direction || 'N/A'}
                  </td>
                  <td>{entryTime ? new Date(entryTime).toLocaleString() : '-'}</td>
                  <td>${entryPrice ? entryPrice.toFixed(4) : '-'}</td>
                  <td>{entryReason || '-'}</td>
                  <td>{exitTime ? new Date(exitTime).toLocaleString() : '-'}</td>
                  <td>${exitPrice ? exitPrice.toFixed(4) : '-'}</td>
                  <td>{exitReason || '-'}</td>
                  <td className={profitLoss && profitLoss >= 0 ? 'profit' : 'loss'}>
                    {profitLoss !== null && profitLoss !== undefined ? `$${profitLoss.toFixed(2)}` : '-'}
                  </td>
                  <td className="profit">
                    {maxProfit !== null && maxProfit !== undefined ? `$${maxProfit.toFixed(2)}` : '-'}
                  </td>
                  <td className="loss">
                    {maxDrawdown !== null && maxDrawdown !== undefined ? `$${Math.abs(maxDrawdown).toFixed(2)}` : '-'}
                  </td>
                  <td className={mlApproved === true ? 'ml-approved' : mlApproved === false ? 'ml-rejected' : ''}>
                    {mlApproved === null || mlApproved === undefined ? 'N/A' : mlApproved ? 'Yes' : 'No'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="page-button"
            >
              Previous
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (
              <button
                key={pageNum}
                onClick={() => handlePageChange(pageNum)}
                className={`page-button ${currentPage === pageNum ? 'active' : ''}`}
              >
                {pageNum}
              </button>
            ))}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="page-button"
            >
              Next
            </button>
        </div>
      )}
    </>
  );
};

export default TradeHistory;
